import styled from '@emotion/styled/macro'
import { RadioGroupItem } from '@radix-ui/react-radio-group'
import React, { Fragment, useRef } from 'react'

import type { Color } from '~/types/Color'
import { Type as ColorType } from '~/types/Color'
import { useFLIP } from '~/hooks/useFLIP'

import { StarBadge } from './StarBadge'

const LAYOUT_ANIM_DURATION = 150

export interface ColorSelectorItemProps {
  color: Color

  selected: Pick<Color, 'id'> | null

  disabled?: boolean
}

export const ColorSelectorItem = React.memo(
  ({ color, disabled, selected }: ColorSelectorItemProps) => {
    return (
      <Option value={`c:${color.id}`} disabled={disabled}>
        <ColorSelectorItemInternal
          color={color}
          selected={selected}
          checked={color.id === selected?.id}
        />
      </Option>
    )
  },
  (prev, next) => {
    return (
      prev.color.id === next.color.id && prev.selected?.id === next.selected?.id
    )
  }
)

interface ColorSelectorItemInternalProps {
  checked: boolean

  color: Color | null
  selected: Pick<Color, 'id'> | null
}

const ColorSelectorItemInternal = ({
  checked,
  color,
  selected
}: ColorSelectorItemInternalProps) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const badgeRef = useRef<SVGSVGElement>(null)

  // eslint-disable-next-line @typescript-eslint/naming-convention -- `_` は未使用変数の命名として一般的
  const { _, ...boxProps } = useFLIP({
    props: {
      'data-checked': checked || undefined,
      // 他のアイテムの選択状態が変わった場合も位置が変わるためアニメーションを行う
      _: selected
    },
    flip: {
      first() {
        return boxRef.current?.getBoundingClientRect()
      },
      last() {
        return boxRef.current?.getBoundingClientRect()
      },
      invert({ first, last }) {
        // 0除算を防ぐ
        if (!first?.width || !last?.height) {
          return {
            sx: 1,
            sy: 1,
            tx: 0
          }
        }

        return {
          sx: first.width / last.width,
          sy: first.height / last.height,
          tx: first.x - last.x
        }
      },
      play({ invert: { sx, sy, tx } }) {
        boxRef.current?.animate(
          [
            { transform: `translateX(${tx}px) scale(${sx}, ${sy})` },
            { transform: 'translateX(0px) scale(1, 1)' }
          ],
          {
            duration: LAYOUT_ANIM_DURATION,
            easing: 'ease'
          }
        )

        // バッジも一緒に変形してしまうため、逆変形を適用する
        if (badgeRef.current) {
          badgeRef.current.animate(
            [
              { transform: `scale(${1 / sx}, ${1 / sy})` },
              { transform: 'scale(1, 1)' }
            ],
            {
              duration: LAYOUT_ANIM_DURATION,
              easing: 'ease'
            }
          )
        }
      }
    }
  })

  return (
    <Fragment>
      <LabelPositioner>
        {!color ? (
          <Label>色を選んでください</Label>
        ) : color.type === ColorType.Basic ? (
          <Label>{color.name}</Label>
        ) : (
          <Label>
            {color.name}
            <AccentText>(期間限定)</AccentText>
          </Label>
        )}
      </LabelPositioner>
      {color ? (
        <Box
          {...boxProps}
          ref={boxRef}
          style={{ '--_box-color': color.colorCode } as React.CSSProperties}
          data-box=""
        >
          {color.type === ColorType.Limited && (
            <LimitedColorBadge ref={badgeRef} />
          )}
        </Box>
      ) : (
        <NullBox {...boxProps} ref={boxRef} data-box="">
          <NullText>未選択</NullText>
        </NullBox>
      )}
    </Fragment>
  )
}

interface NullValueItemProps {
  checked: boolean

  disabled: boolean
}

export const NullValueItem = React.memo(
  ({ checked, disabled }: NullValueItemProps) => {
    return (
      <Option value="null" disabled={disabled}>
        <ColorSelectorItemInternal
          color={null}
          selected={null}
          checked={checked}
        />
      </Option>
    )
  }
)

const Box = styled.div`
  position: relative;
  width: var(--_box-width);
  height: var(--_box-height);
  border: 1px solid var(--border-darker);
  box-sizing: border-box;
  margin-bottom: calc((var(--_box-active-height) - var(--_box-height)) * 0.5);

  cursor: pointer;
  pointer-events: all;

  background-color: var(--_box-color);

  transition: border-radius ${LAYOUT_ANIM_DURATION}ms ease,
    border-color 0.1s ease, background-color 0.1s ease;

  &[data-checked] {
    width: var(--_box-active-width);
    height: var(--_box-active-height);
    margin: 0 var(--_box-gap);

    /* 背景は擬似要素に描画し、本体は透明にする */
    background-color: transparent;
    border-color: transparent;
  }

  &[data-checked]::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: var(--_box-active-height);
    height: var(--_box-active-height);
    transform: translate(-50%, -50%);
    background-color: var(--_box-color);
    border: 1px solid var(--border-darker);
    border-radius: 9999px;
    box-sizing: border-box;
  }
`

const Option = styled(RadioGroupItem)`
  display: flex;
  flex-direction: column;
  height: calc(1em + var(--gutter-medium) + var(--_box-active-height));
  align-items: center;
  justify-content: space-between;

  pointer-events: none;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }

  /* checked の隣接アイテムの内側角だけ丸める */
  &[data-state='checked'] + & ${Box} {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
  }

  /* 先頭(Nullの直後)と末尾のColorSelectorItemの外側角だけ丸める */
  [value='null'] + & ${Box} {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  &:has(+ &[data-state='checked']) ${Box} {
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
  }

  &:last-of-type ${Box} {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`

const NullBox = styled(Box)`
  position: relative;

  transition: opacity 0.1s ease;

  [data-state='unchecked'] > & {
    opacity: 0;
  }

  /* 未選択(Null)アイテムは円形表示をしない */
  &[data-checked]::before {
    display: none;
  }
`

const NullText = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  user-select: none;
  border: 1px solid var(--border-darker);
  border-radius: 999px;
  white-space: nowrap;
`

const LabelPositioner = styled.span`
  display: block;
  width: 0;

  overflow: visible;

  opacity: 0;
  transition: opacity 0.2s ease-out;

  [data-state='checked'] > & {
    opacity: 1;
  }

  [data-state='checked']:focus-visible > & {
    color: var(--link-color);
  }
`

const Label = styled.span`
  display: inline-block;
  white-space: nowrap;
  text-align: center;

  user-select: none;

  transform: translateX(-50%);
`

const AccentText = styled.b`
  margin-left: var(--gutter-extra-small);

  color: var(--red);
  font-weight: var(--default-weight);
`

const LimitedColorBadge = styled(StarBadge)`
  position: absolute;
  top: 3px;
  left: 3px;
  font-size: 12px;

  transform-origin: top left;
`
